<template>
  <div
    class="fixed bottom-0 inset-x-0 px-4 pb-4 sm:inset-0 sm:flex sm:items-center sm:justify-center"
  >
    <!--
    Background overlay, show/hide based on modal state.

    Entering: "ease-out duration-300"
      From: "opacity-0"
      To: "opacity-100"
    Leaving: "ease-in duration-200"
      From: "opacity-100"
      To: "opacity-0"
  -->
    <div class="fixed inset-0 transition-opacity">
      <div class="absolute inset-0 bg-gray-500 opacity-75" @click="close"></div>
    </div>

    <!--
    Modal panel, show/hide based on modal state.

    Entering: "ease-out duration-300"
      From: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      To: "opacity-100 translate-y-0 sm:scale-100"
    Leaving: "ease-in duration-200"
      From: "opacity-100 translate-y-0 sm:scale-100"
      To: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
  -->
    <div
      class="relative bg-white rounded-lg px-4 pt-5 pb-4 overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-headline"
    >
      <div class="sm:flex sm:items-start">
        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <h3
            class="text-lg leading-6 font-medium text-gray-900"
            id="modal-headline"
          >
            Create Campaign
          </h3>
          <div class="mt-2"></div>
        </div>
      </div>
      <div class="sm:ml-4 mt-4">
        <div
          class="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-4"
          v-if="showCSVError"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <!-- Heroicon name: solid/exclamation -->
              <svg
                class="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-yellow-700">
                We are unabled to parse the CSV. Please upload the proper CSV
                file (exported from shopify)
              </p>
            </div>
          </div>
        </div>
        <div
          class="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-4"
          v-if="showLimitWarning"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <!-- Heroicon name: solid/exclamation -->
              <svg
                class="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-yellow-700">
                Whatsapp recommends to send not more than 1000 messages in a day
                with their API.
              </p>
            </div>
          </div>
        </div>
        <div>
          <label
            for="email"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Name
          </label>
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              class="bg-white focus:outline-none focus:shadow-outline border border-gray-300 rounded-md py-2 px-4 block w-full appearance-none leading-normal"
              type="text"
              v-model="form.name"
              ref="name"
              placeholder="Flash sale campaign"
            />
          </div>
        </div>
        <div class="mt-4">
          <label
            for="email"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            <div class="flex justify-between items-center my-2">
              <span>Template</span>
            </div>
          </label>
          <div class="mt-1 relative rounded-md shadow-sm">
            <select
              id="location"
              class="mt-1 form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-300 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
              v-model="form.templateId"
            >
              <option
                v-for="(template, index) in templates"
                :key="index"
                :value="template.elementName"
                >{{ template.name }}</option
              >
            </select>
          </div>
        </div>
        <div class="mt-4">
          <div class="mt-1 relative rounded-md shadow-sm">
            <div class="sm:col-span-6">
              <label
                for="cover-photo"
                class="block text-sm font-medium text-gray-700"
              >
                Import Customers from shopify
              </label>
              <div
                class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md"
              >
                <div class="space-y-1 text-center">
                  <svg
                    class="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></path>
                  </svg>
                  <div class="flex text-sm text-gray-600 justify-center">
                    <label
                      for="file-upload"
                      class="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                    >
                      <span>Upload Customer CSV file</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        accept=".csv"
                        class="sr-only"
                        @change="processFile($event)"
                      />
                    </label>
                    <!-- <p class="pl-1">or drag and drop</p> -->
                  </div>
                  <a
                    href="https://help.shopify.com/en/manual/customers/import-export-customers"
                    target="_blank"
                    class="text-xs text-gray-500 hover:underline"
                  >
                    Learn how to export Customer csv from shopify
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div
            class="flex my-4 justify-center flex-col"
            v-if="form.phoneNumbers.length > 0"
          >
            <label class="block text-sm font-medium text-gray-700">
              Messages will be sent to these numbers
            </label>
            <textarea
              :value="getPhoneNumbers(form.phoneNumbers)"
              rows="3"
              class="max-w-lg shadow-sm block w-full focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border border-gray-300 rounded-md bg-gray-200 cursor-not-allowed p-4"
              disabled
            ></textarea>
          </div>
        </div>
      </div>
      <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
          <button
            type="button"
            class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-indigo-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo transition ease-in-out duration-150 sm:text-sm sm:leading-5 disabled:opacity-75"
            @click="onCreate"
            :disabled="$v.form.$invalid || buttonLoaders.isCreating"
          >
            Send
          </button>
        </span>
        <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
          <button
            type="button"
            class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
            @click="close"
          >
            Cancel
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import CountryCodes from "./countrycodes.json";
import { required } from "vuelidate/lib/validators";

export default {
  components: {},

  data() {
    return {
      name: "",
      body: "",
      form: {
        name: "",
        templateId: "",
        phoneNumbers: []
      },

      showLimitWarning: false,
      showCSVError: false,
      buttonLoaders: {
        isCreating: false
      }
    };
  },
  validations: {
    form: {
      name: {
        required
      },
      templateId: {
        required
      },
      phoneNumbers: {
        required
      }
    }
  },
  computed: {
    templates() {
      const templates = this.$store.getters["Marketing/getTemplates"];
      if (templates) {
        return templates.filter(template => template.status === "approved");
      }
      return [];
    }
  },
  mounted() {
    this.$refs.name.focus();
  },
  methods: {
    close() {
      this.$emit("close");
    },
    onCreate() {
      this.buttonLoaders.isCreating = true;
      this.$store
        .dispatch("Marketing/createCampaign", {
          requestParams: this.form
        })
        .then(() => {
          this.buttonLoaders.isCreating = false;
          this.$emit("create");
        });
    },
    onEdit() {
      this.$emit("edit", { body: this.body, name: this.name });
      this.close();
    },
    insertAtCursor(myValue) {
      const myField = this.$refs.content;
      //IE support
      if (document.selection) {
        myField.focus();
        sel = document.selection.createRange(); // eslint-disable-line
        sel.text = myValue; // eslint-disable-line
      }
      //MOZILLA and others
      else if (myField.selectionStart || myField.selectionStart == "0") {
        var startPos = myField.selectionStart;
        var endPos = myField.selectionEnd;
        myField.value =
          myField.value.substring(0, startPos) +
          myValue +
          myField.value.substring(endPos, myField.value.length);
      } else {
        myField.value += myValue;
      }
      this.body = myField.value;
    },
    processFile(event) {
      this.showCSVError = false;
      this.showLimitWarning = false;
      this.csvFile = event.target.files[0];
      this.$papa.parse(this.csvFile, {
        header: true,
        complete: result => {
          // executed after all files are complete
          const fileResult = result;
          console.log({ fileResult });
          this.phoneNumbersList = [];
          for (const item of fileResult.data) {
            if (item.Phone && item.Phone.length > 0) {
              const phoneNumber = this.getFormattedPhoneNumber(
                item["Country Code"],
                item.Phone
              );
              item.Phone = phoneNumber;
              this.form.phoneNumbers.push({
                phoneNumber,
                countryCode: item["Country Code"],
                firstName: item["First Name"]
              });
            } else if (
              item["Billing Phone"] &&
              item["Billing Phone"].length > 0
            ) {
              const phoneNumber = this.getFormattedPhoneNumber(
                item["Billing Country"],
                item["Billing Phone"]
              );
              item.Phone = phoneNumber;
              this.form.phoneNumbers.push({
                phoneNumber,
                countryCode: item["Billing Country"],
                firstName: item["First Name"]
              });
            } else if (
              item["Shipping Phone"] &&
              item["Shipping Phone"].length > 0
            ) {
              const phoneNumber = this.getFormattedPhoneNumber(
                item["Shipping Country"],
                item["Shipping Phone"]
              );
              item.Phone = phoneNumber;
              this.form.phoneNumbers.push({
                phoneNumber,
                countryCode: item["Shipping Country"],
                firstName: item["First Name"]
              });
            }
          }
          if (this.form.phoneNumbers.length === 0) {
            this.showCSVError = true;
          }
          if (this.form.phoneNumbers.length > 1000) {
            this.showLimitWarning = true;
          }
        }
      });
    },
    sanitizePhoneNumber(phone) {
      let phoneNumber = phone;
      if (phoneNumber.startsWith("+600") || phoneNumber.startsWith("600")) {
        phoneNumber = phoneNumber.replace("600", "60");
      }
      if (phoneNumber.startsWith("+9720") || phoneNumber.startsWith("9720")) {
        phoneNumber = phoneNumber.replace("9720", "972");
      }
      phoneNumber = phoneNumber.replace(/-/g, "");
      return phoneNumber.replace(/ /g, "");
    },
    getFormattedPhoneNumber(countryId, phoneNumber) {
      const country = CountryCodes.find(country => {
        return country.code === countryId;
      });
      if (country) {
        const countryCode = country.dial_code;
        if (!phoneNumber.startsWith(countryCode)) {
          const tempPhone = `${countryCode}${phoneNumber}`;
          return this.sanitizePhoneNumber(tempPhone);
        }
      }
      return this.sanitizePhoneNumber(phoneNumber);
    },
    getPhoneNumbers(phoneNumbers) {
      return phoneNumbers.map(item => item.phoneNumber);
    }
  }
};
</script>

<style lang="scss" scoped></style>
